import {
  useAppointable,
  useAppointableCreate,
  useAppointableEdit,
} from "@gymflow/api";
import { usePortalRoutes } from "apps/portal/src/hooks";
import { useClubSettings } from "apps/portal/src/providers";
import { ToastContext } from "apps/portal/src/providers/ToastProvider/context";
import { RouteFeature, RouteLayout } from "apps/portal/src/routes";
import useGymflowModels from "apps/portal/src/store";
import { useFormik } from "formik";
import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";
import { toFormikValidationSchema } from "zod-formik-adapter";

import { ApiResolverContext } from "../../../../providers/ApiResolverProvider/ApiResolverContext";
import { Button, CloseIcon, Spinner } from "../../../atoms";
import { StepWizardNew } from "../../../organisms";
import { SettingsRoute } from "../../SettingsRoute";
import { AppointableWizardDetailsForm } from "./components/AppointableWizardDetailsForm/AppointableWizardDetailsForm";
import { AppointableWizardPricingStructureForm } from "./components/AppointableWizardPricingStructureForm/AppointableWizardPricingStructureForm";
import { AppointableWizardResourceForm } from "./components/AppointableWizardResourceForm/AppointableWizardResourceForm";
import {
  appointableFormSchema,
  INITIAL_VALUES_APPOINTABLE_FORM,
} from "./constants";
import { AppointableFormValues } from "./types";
import { mapAppointableToInitialValues } from "./utils/mapAppointableToInitialValues";
import { mapFormValuesToPatchDTO } from "./utils/mapFormValuesToPatchDTO";
import { mapFormValuesToPostDTO } from "./utils/mapFormValuesToPostDTO";

type AppointableWizardProps = { mode: "create" | "edit" };

export const AppointableWizard = ({ mode }: AppointableWizardProps) => {
  const { appointableId } = useParams<{ appointableId: string }>();

  const { t } = useTranslation();
  const history = useHistory();
  const { createClubLink } = usePortalRoutes();
  const { clubId } = useContext(ApiResolverContext);
  const { api } = useGymflowModels();
  const { toast } = useContext(ToastContext);

  const { data: appointable, isFetching: isFetchingAppointable } =
    useAppointable({
      api,
      appointableId:
        appointableId && mode === "edit" ? Number(appointableId) : null,
    });

  const { club } = useClubSettings();

  const { mutateAsync: mutateAsyncAppointableCreate } = useAppointableCreate({
    api,
  });

  const { mutateAsync: mutateAsyncAppointableEdit } = useAppointableEdit({
    api,
  });

  const formik = useFormik<AppointableFormValues>({
    initialValues:
      mode === "create"
        ? {
            ...INITIAL_VALUES_APPOINTABLE_FORM,
            pricingStructure: {
              ...INITIAL_VALUES_APPOINTABLE_FORM.pricingStructure,
              price: {
                ...INITIAL_VALUES_APPOINTABLE_FORM.pricingStructure.price,
                salesTaxRate: club?.defaultTaxRate,
              },
            },
          }
        : INITIAL_VALUES_APPOINTABLE_FORM,
    validationSchema: toFormikValidationSchema(appointableFormSchema),
    onSubmit: async (values) => {
      if (mode === "create") {
        await mutateAsyncAppointableCreate(
          mapFormValuesToPostDTO(values, clubId),
        );
        toast({
          message: t(
            "page.settings.tab.appointments.appointableWizard.toast.created",
          ),
          type: "small",
          intent: "success",
        });
      } else if (mode === "edit" && appointable) {
        await mutateAsyncAppointableEdit({
          patchedFields: mapFormValuesToPatchDTO(values, clubId, appointable),
          appointableId: Number(appointableId),
        });
      }

      history.push(
        createClubLink(
          RouteLayout.Staff,
          RouteFeature.Settings,
          SettingsRoute.Appointable,
        ),
      );
    },
  });

  const { setValues } = formik;

  useEffect(() => {
    if (appointable && mode === "edit") {
      const values = mapAppointableToInitialValues(appointable);
      setValues(values);
    }
  }, [appointable, setValues, mode]);

  return (
    <div className="bg-gray-0 flex h-full max-h-full flex-col gap-6 p-4 lg:p-8">
      <div className="flex max-h-full flex-col">
        <div className="flex flex-col gap-2">
          <div className="flex flex-row items-center justify-between gap-2">
            <div className="text-xl font-semibold text-gray-950 lg:text-2xl">
              {mode === "create"
                ? t(
                    "page.settings.tab.appointments.appointableWizard.title.create",
                  )
                : t(
                    "page.settings.tab.appointments.appointableWizard.title.edit",
                  )}
            </div>
            <Button
              intent="transparent"
              size="icon"
              className="cursor-pointer"
              link={createClubLink(
                RouteLayout.Staff,
                RouteFeature.Settings,
                SettingsRoute.Appointable,
              )}
            >
              <CloseIcon className="size-6" pathClassName="stroke-gray-500" />
            </Button>
          </div>
          <div className="max-w-lg text-sm font-medium text-gray-500 lg:font-normal">
            {t("page.settings.tab.appointments.appointableWizard.description")}
          </div>
        </div>
      </div>
      {isFetchingAppointable ? (
        <Spinner />
      ) : (
        <StepWizardNew<AppointableFormValues>
          formik={formik}
          steps={[
            {
              id: "details",
              title: t(
                "page.settings.tab.appointments.appointableWizard.details",
              ),
              form: <AppointableWizardDetailsForm />,
            },
            {
              id: "pricingStructure",
              title: t(
                "page.settings.tab.appointments.appointableWizard.pricingStructure",
              ),
              form: <AppointableWizardPricingStructureForm />,
            },
            {
              id: "resources",
              title: t(
                "page.settings.tab.appointments.appointableWizard.resources",
              ),
              form: (
                <AppointableWizardResourceForm
                  disabledEditResource={appointable?.hasAppointments ?? false}
                />
              ),
            },
          ]}
        />
      )}
    </div>
  );
};
